/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type PropsWithChildren, Suspense, useCallback, useState } from 'react';

import { css, cssMap as unboundCssMap } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { KnowledgeSmartCard as CommonKnowledgeSmartCard } from '@atlassian/lumina';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { getSearchAIAnalyticsContextData } from '../../../ai-answer-dialog/analytics';
import { onExpandButtonClicked } from '../../../ai-answer-dialog/on-expand-button-clicked';
import { SearchAIAnalyticsContext } from '../../../ai-answer-dialog/search-ai-analytics-context';
import { SearchAIAnalyticsContextLocation } from '../../../ai-answer-dialog/search-ai-analytics-context-location';
import type { NLPSearchResultSource, SourceSmartCardType } from '../../../ai-answer-dialog/types';
import { KnowledgeCarousel } from '../../../knowledge-card-components/knowledge-carousel';
import { SAINFeatureControl } from '../../utils/sain-feature-control';
import { ErrorBoundary } from '../error-boundary';
import { KnowledgeSmartCard as LocalKnowledgeSmartCard } from '../knowledge-smart-card/knowledge-smart-card';

import { messages } from './messages';
import { SourceCard } from './source-card';

const CAROUSEL_SOURCE_CARD_WIDTH = 350;
const CAROUSEL_SOURCE_OFFSET = CAROUSEL_SOURCE_CARD_WIDTH + 16; // width + gap

const styles = cssMap({
	cardTitle: {
		marginTop: token('space.150'),
		marginBottom: token('space.200'),
	},
	container: {
		width: '100%',
		marginTop: token('space.075'),
	},
	carouselSourceCardContainer: {
		flexBasis: `${CAROUSEL_SOURCE_CARD_WIDTH}px`,
		flexGrow: 0,
		flexShrink: 0,
		whiteSpace: 'break-spaces',
		width: `${CAROUSEL_SOURCE_CARD_WIDTH}px`,
		maxWidth: `${CAROUSEL_SOURCE_CARD_WIDTH}px`,
		overflow: 'hidden',
	},
});

const unboundStyles = unboundCssMap({
	smartCard: {
		height: '185px',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-smart-link-container="true"], & div[data-testid="embed-card-resolved-view"]': {
			height: '185px',
		},
	},
});

const sourcesContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignContent: 'center',
	columnGap: token('space.050'),
});

const freeFlowLinksStyles = css({
	display: 'inline',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 2,
	marginRight: token('space.075'),
});

const Sources = ({
	sources,
	searchSessionId,
	onClickHandler,
	allowFreeflowLayout,
	verticalLayout,
	enableSmartLinkSources = true,
}: {
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	onClickHandler: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	allowFreeflowLayout: boolean;
	verticalLayout: boolean;
	enableSmartLinkSources?: boolean;
}) => {
	const [showAllSources, setShowAllSources] = useState(false);
	const { fireAnalyticsEvent, commonAttributes } = useAIAnswerContext();
	const canExpand = sources.length > 1 && verticalLayout;
	const visibleSources = showAllSources || !verticalLayout ? sources : sources.slice(0, 1);

	const onExpandButtonClick = useCallback(() => {
		fireAnalyticsEvent(
			onExpandButtonClicked({ ...commonAttributes, expandButtonSource: 'sources' }),
		);
		setShowAllSources(!showAllSources);
	}, [fireAnalyticsEvent, commonAttributes, showAllSources]);

	return (
		<Fragment>
			{visibleSources.map((source, index) => (
				<div
					key={`ai-answer-source-${index}`}
					css={[sourcesContainerStyles, allowFreeflowLayout && freeFlowLinksStyles]}
				>
					<SourceCard
						index={index}
						key={`search-ai-answer-source-${index}`}
						source={source}
						searchSessionId={searchSessionId}
						onClickHandler={onClickHandler}
						enableSmartLinkSources={enableSmartLinkSources}
					/>
					{index === 0 && canExpand && (
						<IconButton
							onClick={onExpandButtonClick}
							icon={(iconProps) =>
								showAllSources ? (
									<ChevronDownIcon
										{...iconProps}
										color="currentColor"
										testId="collapse-sources-icon"
										size="small"
									/>
								) : (
									<ChevronRightIcon
										{...iconProps}
										color="currentColor"
										testId="expand-sources-icon"
										size="small"
									/>
								)
							}
							label={
								showAllSources
									? expValEquals('a11y-fixes-week3-may-2026', 'isEnabled', true)
										? 'Collapse sources'
										: 'Collapse sources button'
									: expValEquals('a11y-fixes-week3-may-2026', 'isEnabled', true)
										? 'Expand sources'
										: 'Expand sources button'
							}
							spacing="compact"
							appearance="subtle"
						/>
					)}
				</div>
			))}
		</Fragment>
	);
};

const LayoutWrapper = (
	props: PropsWithChildren<{ allowFreeflowLayout: boolean; verticalLayout: boolean }>,
) => {
	if (props.allowFreeflowLayout) {
		return <div>{props.children}</div>;
	} else if (props.verticalLayout) {
		return <Stack space="space.075">{props.children}</Stack>;
	} else {
		return (
			<Inline space="space.075" shouldWrap>
				{props.children}
			</Inline>
		);
	}
};

type AISourcesProps = {
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	title?: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	onClickHandler?: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	allowFreeflowLayout?: boolean;
	verticalLayout?: boolean;
	limit?: number;
	enableSmartLinkSources?: boolean;
	isReadingAids?: boolean;
};

export const AISources: (props: AISourcesProps) => JSX.Element = (props: AISourcesProps) => {
	const {
		sources: ogSources,
		searchSessionId,
		title,
		onClickHandler = () => {},
		allowFreeflowLayout = false,
		verticalLayout = false,
		limit,
		enableSmartLinkSources,
		isReadingAids = false,
	} = props;
	const sourcesTitle = title ?? messages.sources_title;

	const sources = limit ? ogSources.slice(0, limit) : ogSources;

	const { commonAttributes } = useAIAnswerContext();

	const MaybeSmartCardProvider = enableSmartLinkSources !== false ? SmartCardProvider : Fragment;

	if (!sources.length) {
		return <Fragment />;
	}

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Suspense fallback={<Fragment />}>
				<Box xcss={styles.cardTitle}>
					<Heading as="h2" size="xxsmall">
						<Text color="color.text.subtlest" size="small" weight="bold">
							<FormattedMessage {...sourcesTitle} values={{ numSources: sources.length }} />
						</Text>
					</Heading>
				</Box>
				<Box xcss={styles.container}>
					<LayoutWrapper allowFreeflowLayout={allowFreeflowLayout} verticalLayout={verticalLayout}>
						<SearchAIAnalyticsContext
							data={getSearchAIAnalyticsContextData({
								location: isReadingAids
									? SearchAIAnalyticsContextLocation.AI_DEFINITION
									: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
								commonAttributes,
							})}
						>
							<MaybeSmartCardProvider>
								<Sources
									sources={sources}
									searchSessionId={searchSessionId}
									onClickHandler={onClickHandler}
									allowFreeflowLayout={allowFreeflowLayout}
									verticalLayout={verticalLayout}
									enableSmartLinkSources={enableSmartLinkSources}
								/>
							</MaybeSmartCardProvider>
						</SearchAIAnalyticsContext>
					</LayoutWrapper>
				</Box>
			</Suspense>
		</ErrorBoundary>
	);
};

function AISourcesCarouselHeading({ numSources }: { numSources: number }) {
	const headingMessage = SAINFeatureControl.isSAINUXUpliftEnabled()
		? messages.related_links_title
		: messages.sources_title;
	return (
		<Heading size="xsmall">
			<FormattedMessage {...headingMessage} values={{ numSources }} />
		</Heading>
	);
}

type AISourcesCarouselProps = {
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	onClickHandler: ({
		index,
		source,
		hostname,
		sourceCardType,
	}: {
		index: number;
		source: NLPSearchResultSource;
		hostname?: string;
		sourceCardType: SourceSmartCardType;
	}) => void;
	isReadingAids?: boolean;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function AISourcesCarousel(props: AISourcesCarouselProps): JSX.Element {
	const { commonAttributes } = useAIAnswerContext();
	const { sources, searchSessionId, onClickHandler, isReadingAids = false } = props;

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Suspense fallback={<Fragment />}>
				<Box xcss={styles.container} testId="ai-sources-carousel">
					<SearchAIAnalyticsContext
						data={getSearchAIAnalyticsContextData({
							location: isReadingAids
								? SearchAIAnalyticsContextLocation.AI_DEFINITION
								: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
							commonAttributes,
						})}
					>
						<SmartCardProvider>
							<KnowledgeCarousel
								heading={<AISourcesCarouselHeading numSources={sources.length} />}
								scrollOffset={CAROUSEL_SOURCE_OFFSET}
							>
								{sources.map((source, index) => (
									<Box
										key={`search-ai-answer-source-${index}`}
										xcss={cx(styles.carouselSourceCardContainer, unboundStyles.smartCard)}
									>
										{fg('kd_use_common_knowledge_card') ? (
											<CommonKnowledgeSmartCard
												index={index}
												source={source}
												searchSessionId={searchSessionId}
												onClickHandler={onClickHandler}
											/>
										) : (
											<LocalKnowledgeSmartCard
												index={index}
												source={source}
												searchSessionId={searchSessionId}
												onClickHandler={onClickHandler}
											/>
										)}
									</Box>
								))}
							</KnowledgeCarousel>
						</SmartCardProvider>
					</SearchAIAnalyticsContext>
				</Box>
			</Suspense>
		</ErrorBoundary>
	);
}
